import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function fetchClockStatus(params = {}) {
  const res = await axios.get(`${API_URL}/api/time-tracking/status`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function postClock({
  eventType,
  workMode = "presencial",
  notes,
  pauseType,
  latitude,
  longitude,
  accuracyMeters,
  geoSource,
  clientSource,
}) {
  const res = await axios.post(
    `${API_URL}/api/time-tracking/clock`,
    {
      eventType,
      workMode,
      notes: notes ?? null,
      pauseType: pauseType ?? null,
      geoSource: geoSource || "unavailable",
      latitude: latitude ?? null,
      longitude: longitude ?? null,
      accuracyMeters: accuracyMeters ?? null,
      clientSource: clientSource ?? "web",
    },
    { headers: authHeaders() },
  );
  return res.data;
}

export async function fetchClockEntries(params = {}) {
  const res = await axios.get(`${API_URL}/api/time-tracking/entries`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function fetchDailyBoard(params = {}) {
  const res = await axios.get(`${API_URL}/api/time-tracking/daily-board`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function correctClockEntry(id, body) {
  const res = await axios.post(
    `${API_URL}/api/time-tracking/entries/${id}/correct`,
    body,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function createManualClockEntry(body) {
  const res = await axios.post(
    `${API_URL}/api/time-tracking/entries/manual`,
    body,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function fetchCorrectionRequests(params = {}) {
  const res = await axios.get(
    `${API_URL}/api/time-tracking/correction-requests`,
    { params, headers: authHeaders() },
  );
  return res.data;
}

export async function createCorrectionRequest(body) {
  const res = await axios.post(
    `${API_URL}/api/time-tracking/correction-requests`,
    body,
    { headers: authHeaders() },
  );
  notifyTimeTrackingPendingUpdated();
  return res.data;
}

export async function reviewCorrectionRequest(id, body) {
  const res = await axios.patch(
    `${API_URL}/api/time-tracking/correction-requests/${id}/review`,
    body,
    { headers: authHeaders() },
  );
  notifyTimeTrackingPendingUpdated();
  return res.data;
}

export async function cancelCorrectionRequest(id) {
  const res = await axios.patch(
    `${API_URL}/api/time-tracking/correction-requests/${id}/cancel`,
    {},
    { headers: authHeaders() },
  );
  notifyTimeTrackingPendingUpdated();
  return res.data;
}

export async function fetchTimeOffRequests(params = {}) {
  const res = await axios.get(`${API_URL}/api/time-tracking/time-off`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function fetchVacationCalendar(params = {}) {
  const res = await axios.get(`${API_URL}/api/time-tracking/time-off/calendar`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function createTimeOffRequest(body) {
  const res = await axios.post(
    `${API_URL}/api/time-tracking/time-off`,
    body,
    { headers: authHeaders() },
  );
  notifyTimeTrackingPendingUpdated();
  return res.data;
}

export async function reviewTimeOffRequest(id, body) {
  const res = await axios.patch(
    `${API_URL}/api/time-tracking/time-off/${id}/review`,
    body,
    { headers: authHeaders() },
  );
  notifyTimeTrackingPendingUpdated();
  return res.data;
}

export async function cancelTimeOffRequest(id) {
  const res = await axios.patch(
    `${API_URL}/api/time-tracking/time-off/${id}/cancel`,
    {},
    { headers: authHeaders() },
  );
  notifyTimeTrackingPendingUpdated();
  return res.data;
}

export async function fetchOvertimeEntries(params = {}) {
  const res = await axios.get(`${API_URL}/api/time-tracking/overtime`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function createOvertimeEntry(body) {
  const res = await axios.post(
    `${API_URL}/api/time-tracking/overtime`,
    body,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function reviewOvertimeEntry(id, body) {
  const res = await axios.patch(
    `${API_URL}/api/time-tracking/overtime/${id}/review`,
    body,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function fetchTimeBalance(params = {}) {
  const res = await axios.get(`${API_URL}/api/time-tracking/balance`, {
    params,
    headers: authHeaders(),
  });
  return res.data;
}

export async function fetchVacationBalances(params = {}) {
  const res = await axios.get(
    `${API_URL}/api/time-tracking/vacation-balances`,
    {
      params,
      headers: authHeaders(),
    },
  );
  return res.data;
}

export async function fetchTimeTrackingEmployees() {
  const res = await axios.get(`${API_URL}/api/time-tracking/employees`, {
    headers: authHeaders(),
  });
  return res.data;
}

function normalizePendingCount(data) {
  const corrections = Number(data?.corrections) || 0;
  const timeOff = Number(data?.timeOff) || 0;
  const total = Number(data?.total);
  return {
    corrections,
    timeOff,
    total: Number.isFinite(total) ? total : corrections + timeOff,
  };
}

/** Peticiones pendientes de revisión (correcciones + vacaciones/permisos). */
export async function fetchPendingTimeTrackingRequestsCount() {
  try {
    const res = await axios.get(`${API_URL}/api/time-tracking/pending-count`, {
      headers: authHeaders(),
    });
    return normalizePendingCount(res.data);
  } catch (err) {
    if (err?.response?.status !== 404) throw err;
    const [corrections, timeOff] = await Promise.all([
      fetchCorrectionRequests({ status: "pending" }),
      fetchTimeOffRequests({ status: "pending" }),
    ]);
    return normalizePendingCount({
      corrections: corrections?.length || 0,
      timeOff: timeOff?.length || 0,
    });
  }
}

export function notifyTimeTrackingPendingUpdated() {
  window.dispatchEvent(new Event("time-tracking-pending-updated"));
}
